/**
 * `/services` — the perf page.
 *
 * ---------------------------------------------------------------------------
 * THE LATENCY SPREAD
 * ---------------------------------------------------------------------------
 * The p95 column is the whole reason this tranche has a verification contract.
 * `below_request_floor` prints as "below request floor (12 of 100)" and
 * `empty:no_series` prints as "empty: no series" — both as text, both never as a
 * number, and both never as the green tick a `?? 0` would produce.
 *
 * On top of that this page draws the p50/p95/p99 spread on a log track, because
 * three numbers in three cells do not show what an operator actually reads them
 * for: the SHAPE of the tail. A service whose p50 and p99 sit on top of each
 * other is healthy in a way that "210 ms / 940 ms / 1.82 s" does not convey at a
 * glance. The track's ceiling is the histogram's own top finite bucket, so the
 * scale is a property of the measurement rather than a threshold this page
 * invented — and a percentile that is not a number puts NO marker on the track:
 * the track goes dotted and empty, and the word is printed instead. There is no position
 * on that scale that means "we did not measure", so none is drawn.
 *
 * The "last perf sweep said" column carries the sweep's own stored string
 * verbatim next to the live reading. If the two ever disagree, that is visible
 * on screen rather than hidden behind one of them.
 */

import type { ReactNode } from 'react';

import { fullTime, relativeTime } from '../lib/format';
import { epistemicFor, formatNumber, formatSeconds, labelFor } from '../lib/signal';
import { Icon } from '../components/icons';
import type { PercentileSignalView, Signal } from '../lib/signal';
import {
  Absent,
  Card,
  Eyebrow,
  Ident,
  PercentileValue,
  Placeholder,
  SignalValue,
  Skeleton,
  StatusPill,
  statusTextClass,
} from '../components/primitives';

interface ServiceCard {
  namespace: string;
  ingress: string;
  label: string;
  requestsLifetime: Signal<number>;
  rps: Signal<number>;
  p50: PercentileSignalView;
  p95: PercentileSignalView;
  p99: PercentileSignalView;
  fiveXxPercent: Signal<number>;
  fiveXxIncrease1h: Signal<number>;
  four99: Signal<number>;
  placement: Signal<string[]>;
  lastSweepP95: string | null;
  lastSweepSilence: string | null;
  lastPerfNote: string | null;
  lastPerfRunAt: string | null;
  verdictRank: number;
}

export interface ServicesData {
  generatedAt: string;
  watchListSource: 'config' | 'default';
  services: ServiceCard[];
  lastPerfSweepAt: string | null;
}

/* ------------------------------------------------------------------ *
 * The spread track
 * ------------------------------------------------------------------ */

/** Bottom of the log track, in seconds. Below this everything is "fast". */
const TRACK_FLOOR = 0.005;

function logPosition(seconds: number, ceiling: number): number {
  const lo = Math.log10(TRACK_FLOOR);
  const hi = Math.log10(ceiling);
  const v = Math.log10(Math.max(TRACK_FLOOR, seconds));
  return Math.min(1, Math.max(0, (v - lo) / (hi - lo)));
}

/**
 * p50 / p95 / p99 on one log track.
 *
 * Markers are drawn ONLY for `ok` percentiles. `above_top_bucket` gets an arrow
 * pinned past the ceiling (its meaning is "further right than this scale goes",
 * which an arrow says exactly); every other non-ok status draws nothing at all
 * and empties the track, because there is no honest x for "we did not measure".
 */
function LatencySpread({
  p50,
  p95,
  p99,
}: {
  p50: PercentileSignalView;
  p95: PercentileSignalView;
  p99: PercentileSignalView;
}): ReactNode {
  const marks = [
    { key: 'p50', p: p50, opacity: 'opacity-45' },
    { key: 'p95', p: p95, opacity: '' },
    { key: 'p99', p: p99, opacity: 'opacity-70' },
  ];
  const ceilingRaw = [p50, p95, p99].find((p) => p.status === 'above_top_bucket')?.topBucketLe;
  const ceiling = ceilingRaw === undefined ? 10 : Number(ceilingRaw);
  const usable = Number.isFinite(ceiling) && ceiling > TRACK_FLOOR ? ceiling : 10;

  const plotted = marks.filter((m) => m.p.status === 'ok' && typeof m.p.seconds === 'number');
  const overflow = marks.filter((m) => m.p.status === 'above_top_bucket');
  const nothingPlottable = plotted.length === 0 && overflow.length === 0;

  /*
   * When nothing can be plotted the track goes DOTTED and empty — "there is
   * nothing on this scale" — and it takes the ink or hue of p95's own status,
   * so an `empty` track is ink and an `error` track is red. Painting every
   * empty track amber would say `unproven` about services that are nothing of
   * the kind, which is the exact class of lie this vocabulary exists to
   * prevent.
   */
  const emptyTone = statusTextClass(p95.status);

  const description = marks
    .map(({ key, p }) =>
      p.status === 'ok' && typeof p.seconds === 'number'
        ? `${key} ${formatSeconds(p.seconds)}`
        : `${key} ${labelFor(p.status)}`,
    )
    .join(', ');

  return (
    <div>
      <div
        role="img"
        aria-label={`latency spread on a logarithmic scale from 5 ms to ${String(usable)} s: ${description}`}
        className={`relative h-6 min-w-36 rounded-md border border-line ${
          nothingPlottable ? `epi-unmeasured ${emptyTone}` : 'bg-surface-2'
        }`}
      >
        {/* Decade gridlines: 10 ms, 100 ms, 1 s. */}
        {[0.01, 0.1, 1].map((decade) =>
          decade < usable ? (
            <span
              key={decade}
              aria-hidden="true"
              className="absolute top-0 h-full w-px bg-line"
              style={{ left: `${String(logPosition(decade, usable) * 100)}%` }}
            />
          ) : null,
        )}

        {plotted.map(({ key, p, opacity }) => (
          <span
            key={key}
            aria-hidden="true"
            title={`${key}: ${formatSeconds(p.seconds ?? 0)}`}
            className={`absolute top-1/2 h-3.5 w-[3px] -translate-x-1/2 -translate-y-1/2 rounded-full ${
              key === 'p95' ? 'bg-ink' : 'bg-ink-3'
            } ${opacity}`}
            style={{ left: `${String(logPosition(p.seconds ?? 0, usable) * 100)}%` }}
          />
        ))}

        {overflow.length > 0 && (
          <span
            aria-hidden="true"
            title={`${overflow.map((m) => m.key).join(', ')}: above the top finite bucket`}
            className="absolute right-1 top-1/2 -translate-y-1/2 text-bad"
          >
            <Icon name="aboveTopBucket" size={13} />
          </span>
        )}
      </div>
      <div className="mt-0.5 flex justify-between text-[9px] leading-none text-ink-3">
        <span>5 ms</span>
        <span>100 ms</span>
        <span className="num">{usable} s</span>
      </div>
    </div>
  );
}

/** The sweep's stored string, rendered as words with its own texture. */
function SweepVerdict({ value }: { value: string | null }): ReactNode {
  if (value === null) {
    return (
      <Absent size="sm" title="the perf sweep has not recorded this service yet">
        no reading
      </Absent>
    );
  }
  // `empty:no_series` is one string carrying two facts. Split it rather than
  // print a colon-delimited token at an operator.
  const status = value.startsWith('empty:') ? 'empty' : value;
  const text = value.startsWith('empty:')
    ? `empty: ${value.slice('empty:'.length).replace(/_/g, ' ')}`
    : labelFor(value);
  const epistemic = epistemicFor(status);
  return (
    <span
      className={`inline-flex items-center gap-1 whitespace-nowrap rounded border px-1.5 text-2xs ${statusTextClass(status)} ${
        epistemic === 'measured'
          ? 'epi-measured'
          : epistemic === 'unmeasurable'
            ? 'epi-unmeasurable'
            : 'epi-unmeasured'
      }`}
      title={`the perf sweep recorded "${value}"`}
    >
      {text}
    </span>
  );
}

/* ------------------------------------------------------------------ *
 * Page
 * ------------------------------------------------------------------ */

export function ServicesPage({ data }: { data: ServicesData | null }): ReactNode {
  if (data === null) {
    return (
      <Skeleton label="the watched services" shapes={['h-44', 'h-44', 'h-44']} />
    );
  }

  /*
    Sweep-level, not per-service. The API repeats the perf sweep's note on every
    service row; rendered inside each card it reads as a verdict ABOUT that
    service — "legal-expert p95 is above the top bucket" printed under the
    horeca card is worse than not printing it at all. It belongs here, once,
    labelled with whose note it is.
  */
  const perfNote = data.services[0]?.lastPerfNote ?? null;
  const perfRunAt = data.services[0]?.lastPerfRunAt ?? null;

  return (
    <div className="space-y-3 md:space-y-4">
      <Card
        title="Watch list"
        icon="services"
        subtitle={
          data.watchListSource === 'config'
            ? 'from MW_WATCHED_INGRESSES'
            : 'the PLAN §3 default list (MW_WATCHED_INGRESSES is unset)'
        }
        right={
          <StatusPill status="ok">{`${String(data.services.length)} watched`}</StatusPill>
        }
        footer={
          perfNote !== null ? (
            <>
              <Eyebrow>
                the perf sweep&rsquo;s note
                {perfRunAt === null ? '' : ` · ${relativeTime(perfRunAt)}`}
              </Eyebrow>
              <p className="mt-1 leading-relaxed text-ink-2">{perfNote}</p>
            </>
          ) : undefined
        }
      >
        <p className="flex flex-wrap items-center gap-x-2 text-xs text-ink-3">
          <Icon name="clock" size={13} className="text-ink-4" />
          last perf sweep{' '}
          {data.lastPerfSweepAt === null ? (
            <Absent size="sm">never run</Absent>
          ) : (
            <span className="num font-medium text-ink-2" title={fullTime(data.lastPerfSweepAt)}>
              {relativeTime(data.lastPerfSweepAt)}
            </span>
          )}
          <span className="text-ink-4">·</span> worst first
        </p>
      </Card>

      {data.services.length === 0 && (
        <Placeholder status="unproven" title="nothing is being watched">
          The perf sweep was given no ingresses, so its p95 dimension is <code>unproven</code> — a
          cluster-wide percentile would hide a per-service breach.
        </Placeholder>
      )}

      {/*
        ONE presentation, at every width — a card per service, in a grid.

        This page used to carry a ten-column table above `xl` and a card list
        below it. Six of those ten columns held CHIPS rather than numbers, which
        is a list wearing a table's clothes; and with the chips set not to wrap,
        the ten columns needed ~1400 px of a ~1150 px content area, so `499`,
        `sweep said` and `placement` were clipped off the right-hand edge of the
        card — the three columns an operator opens this page for, invisible,
        with no scrollbar to hint at them.

        The card carries every field the table did, wraps instead of clipping,
        and reads the same on a phone and on a 27". Five watched ingresses is a
        set you scan, not a dataset you sort.
      */}
      <ul className="grid gap-4 lg:grid-cols-2">
        {data.services.map((service) => (
          <li key={service.label}>
            <ServiceCardView service={service} />
          </li>
        ))}
      </ul>
    </div>
  );
}

function ServiceCardView({ service }: { service: ServiceCard }): ReactNode {
  return (
    <article className="rounded-card border border-line bg-surface">
      <header className="flex items-start justify-between gap-3 px-5 pb-3 pt-5">
        {/*
          NOT truncated: at 390 px `legal-expert/legal-expert-backend`
          truncates to `legal-expert/l…`, which does not identify the ingress —
          and this page exists to tell services apart.
        */}
        {/*
          NOT truncated and NOT `break-all`: at 390 px `break-all` split
          `legal-expert-backend` as `legal-expert-ba / ckend`. `break-words`
          takes the existing `/` and `-` opportunities first and only breaks
          mid-token when a single token genuinely does not fit.
        */}
        <Ident className="block break-words leading-snug">{service.label}</Ident>
        <span className="shrink-0">
          <StatusPill status={service.p95.status} size="sm">
            {service.p95.status === 'ok' ? 'p95 read' : labelFor(service.p95.status)}
          </StatusPill>
        </span>
      </header>

      <div className="px-5 pb-1">
        <LatencySpread p50={service.p50} p95={service.p95} p99={service.p99} />
      </div>

      {/*
        Three ROWS on a phone, three COLUMNS from `sm` up.
        At 390 px a third of the card is ~110 px and "below request floor" is
        ~130 px, so a three-column grid either wraps the word into a three-line
        tower or — with the wrap suppressed — spills it over the neighbouring
        percentile. A row gives the value the full width and neither happens.
      */}
      <dl className="mt-4 divide-y divide-line border-y border-line sm:grid sm:grid-cols-3 sm:divide-x sm:divide-y-0">
        <PercentileCell label="p50" p={service.p50} />
        <PercentileCell label="p95" p={service.p95} emphasis />
        <PercentileCell label="p99" p={service.p99} />
      </dl>

      <dl className="grid grid-cols-3 gap-x-4 px-5 py-4 text-xs">
        <Cell label="rps">
          <SignalValue signal={service.rps} format={(v) => formatNumber(v as number, 2)} />
        </Cell>
        <Cell label="5xx share">
          <SignalValue
            signal={service.fiveXxPercent}
            format={(v) => `${formatNumber(v as number, 2)}%`}
          />
        </Cell>
        <Cell label="499s (1 h)">
          <SignalValue signal={service.four99} format={(v) => formatNumber(v as number, 0)} />
        </Cell>
      </dl>

      <div className="flex flex-wrap items-center justify-between gap-3 border-t border-line px-5 py-3 text-2xs text-ink-3">
        <span className="flex items-center gap-1.5">
          <span className="text-ink-3">on</span>
          <SignalValue signal={service.placement} format={(v) => (v as string[]).join(', ')} />
        </span>
        <span className="flex items-center gap-1.5">
          <span className="text-ink-3">sweep said</span>
          <SweepVerdict value={service.lastSweepP95} />
        </span>
      </div>
    </article>
  );
}

function PercentileCell({
  label,
  p,
  emphasis = false,
}: {
  label: string;
  p: PercentileSignalView;
  emphasis?: boolean;
}): ReactNode {
  return (
    <div
      className={`flex min-w-0 items-baseline justify-between gap-3 px-5 py-2.5 sm:block sm:px-4 sm:py-3 ${
        emphasis ? 'bg-surface-2/50' : ''
      }`}
    >
      <dt className="text-[10px] uppercase tracking-[0.08em] text-ink-3">{label}</dt>
      <dd className="min-w-0 text-right leading-tight sm:mt-1 sm:text-left">
        {/*
          p95 prints the FULL sentence — "below request floor (12 of 100)" —
          because it is the metric the perf sweep judges on and those counts are
          what make the state actionable. p50 and p99 are context and take the
          compact word; their counts stay in the tooltip and the sr-only text.
        */}
        <PercentileValue p={p} emphasis={emphasis} compact={!emphasis} />
      </dd>
    </div>
  );
}

function Cell({ label, children }: { label: string; children: ReactNode }): ReactNode {
  return (
    <div className="min-w-0">
      <dt className="text-[10px] uppercase tracking-[0.08em] text-ink-3">{label}</dt>
      <dd className="mt-0.5 text-ink">{children}</dd>
    </div>
  );
}
